import { apiRequest, getApiBaseUrl } from "@/shared/lib/apiClient";
import { getAuthToken, getStoredAccountType } from "../auth";
import type { InsurerCompany, NetworkHospital } from "./insurerApi";

export type ClaimsStatus = "pending" | "controlled" | "anomaly";

export type ControlQueueItem = {
  id: number;
  company: string;
  hospital: string;
  period_start: string | null;
  period_end: string | null;
  total_amount: number;
  approved_amount: number | null;
  currency: string;
  audited_at: string | null;
  claims_status: ClaimsStatus;
  claims_checked_at: string | null;
  /** Niveau de traitement en cours et son échéance. */
  deadline: import("./types").InvoiceDeadline | null;
};

export type ControlCheck = {
  key: string;
  label: string;
  status: "ok" | "warn" | "fail" | "na";
  message: string;
};

export type ControlDetail = {
  invoice: {
    id: number;
    company: string;
    hospital: string;
    period_start: string | null;
    period_end: string | null;
    total_amount: number;
    approved_amount: number | null;
    currency: string;
    audit_note: string;
    invoice_status: string;
    claims_status: ClaimsStatus;
    claims_notes: string;
    /** Date d'envoi de la note de paiement (après validation finale). */
    payment_note_sent_at: string | null;
    deadline: import("./types").InvoiceDeadline | null;
  };
  /** Codification de l'Audit, par patient et par code acte. */
  coding: PatientCoding[];
  amounts: { billed: number; retained: number; refracted: number; covered: number };
  documents: Array<{ id: number; type: string; name: string; declared_total: number | null }>;
  checks: ControlCheck[];
  /** Lignes facturées au-dessus de la grille tarifaire en vigueur. */
  price_lines: Array<{ act: string; billed_unit_price: number; grid_price: number; quantity: number; excess: number }>;
  /** Actes non couverts et dépassements de plafonds du package. */
  limit_issues: string[];
};

export type CoverageImportResult = {
  rows: number;
  created: number;
  updated: number;
  deactivated: number;
  dependants_created?: number;
  dependants_updated?: number;
  errors: string[];
};

/* Agents couverts et ayants droit d'une entreprise (Sinistre). */

export type Dependant = {
  id: number;
  name: string;
  relationship: "spouse" | "child" | "parent" | "other";
  relationship_label: string;
  birthdate: string | null;
  gender: "M" | "F" | null;
  status: "active" | "inactive";
};

export type CoveredAgent = {
  id: number;
  matricule: string;
  name: string;
  birthdate: string | null;
  gender: "M" | "F" | null;
  phone: string | null;
  status: "active" | "inactive";
  /** Code SafeCheck de la famille (SFC-XXXX), partagé par l'agent et ses ayants droit. */
  family_code?: string;
  has_photo?: boolean;
  /** Package choisi pour cet agent ; null = package unique de l'entreprise, ou aucun. */
  package_id: number | null;
  package_name: string | null;
  dependants: Dependant[];
};

export type AgentPayload = Pick<CoveredAgent, "matricule" | "name" | "birthdate" | "gender" | "phone" | "status">;

export type DependantPayload = Pick<Dependant, "name" | "relationship" | "birthdate" | "gender" | "status">;

/** Portefeuille d'une entreprise : l'Admin y accède hors assureur (catalogue), le Sinistre dans son assureur. */
function companyBase(companyId: number): string {
  return getStoredAccountType() === "admin" ? `/admin/companies/${companyId}` : `/claims/portfolio/companies/${companyId}`;
}

export async function fetchCompanyAgents(
  companyId: number,
  search = "",
): Promise<{
  agents: CoveredAgent[];
  packages: Array<{ id: number; name: string }>;
  relationships: Array<{ value: Dependant["relationship"]; label: string }>;
}> {
  const qs = search.trim() ? `?q=${encodeURIComponent(search.trim())}` : "";
  return apiRequest(`${companyBase(companyId)}/agents${qs}`, { auth: true });
}

export async function saveAgentPackage(companyId: number, employeeId: number, packageId: number | null): Promise<void> {
  await apiRequest(`${companyBase(companyId)}/agents/${employeeId}/package`, {
    method: "PUT",
    auth: true,
    body: { package_id: packageId },
  });
}

export async function saveCompanyAgent(companyId: number, payload: AgentPayload, employeeId?: number): Promise<CoveredAgent> {
  const response = await apiRequest<{ agent: CoveredAgent }>(
    `${companyBase(companyId)}/agents${employeeId ? `/${employeeId}` : ""}`,
    { method: employeeId ? "PUT" : "POST", auth: true, body: payload },
  );
  return response.agent;
}

/** Photo d'un agent (Admin) : enregistrement puis affichage via une URL locale. */
export async function uploadAgentPhoto(companyId: number, employeeId: number, file: File): Promise<void> {
  const form = new FormData();
  form.append("photo", file);
  await apiRequest(`${companyBase(companyId)}/agents/${employeeId}/photo`, { method: "POST", auth: true, body: form });
}

export async function fetchAgentPhotoUrl(companyId: number, employeeId: number): Promise<string | null> {
  const token = getAuthToken();
  if (!token) return null;
  const response = await fetch(`${getApiBaseUrl()}${companyBase(companyId)}/agents/${employeeId}/photo`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "image/*" },
  });
  return response.ok ? URL.createObjectURL(await response.blob()) : null;
}

export async function saveAgentDependant(
  companyId: number,
  employeeId: number,
  payload: DependantPayload,
  dependantId?: number,
): Promise<CoveredAgent> {
  const response = await apiRequest<{ agent: CoveredAgent }>(
    `${companyBase(companyId)}/agents/${employeeId}/dependants${dependantId ? `/${dependantId}` : ""}`,
    { method: dependantId ? "PUT" : "POST", auth: true, body: payload },
  );
  return response.agent;
}

export type CoverageImport = CoverageImportResult & {
  id: number;
  file_name: string;
  imported_at: string;
};

/* Contrôle Sinistre */

export async function fetchControlQueue(status: ClaimsStatus | "all"): Promise<ControlQueueItem[]> {
  const response = await apiRequest<{ invoices: ControlQueueItem[] }>(`/claims/control?status=${status}`, {
    auth: true,
  });
  return response.invoices;
}

export async function fetchControlDetail(id: number): Promise<ControlDetail> {
  return apiRequest<ControlDetail>(`/claims/control/${id}`, { auth: true });
}

export async function decideControl(
  id: number,
  decision: Exclude<ClaimsStatus, "pending">,
  notes: string,
): Promise<ControlDetail> {
  return apiRequest<ControlDetail>(`/claims/control/${id}/decision`, {
    method: "POST",
    auth: true,
    body: { decision, notes },
  });
}

/* Référentiels de l'assureur actif */

export async function fetchClaimsCompanies(): Promise<InsurerCompany[]> {
  const response = await apiRequest<{ companies: InsurerCompany[] }>(`${getStoredAccountType() === "admin" ? "/admin" : "/claims"}/portfolio/companies`, { auth: true });
  return response.companies;
}


export async function importCoverageList(
  companyId: number,
  file: File,
  deactivateMissing: boolean,
): Promise<CoverageImportResult> {
  const form = new FormData();
  form.append("file", file);
  form.append("deactivate_missing", deactivateMissing ? "1" : "0");
  const response = await apiRequest<{ result: CoverageImportResult }>(
    `${companyBase(companyId)}/coverage-list`,
    { method: "POST", auth: true, body: form },
  );
  return response.result;
}

export async function fetchCoverageHistory(companyId: number): Promise<CoverageImport[]> {
  const response = await apiRequest<{ imports: CoverageImport[] }>(
    `${companyBase(companyId)}/coverage-list`,
    { auth: true },
  );
  return response.imports;
}

export async function fetchClaimsNetwork(): Promise<NetworkHospital[]> {
  const response = await apiRequest<{ hospitals: NetworkHospital[] }>(`${getStoredAccountType() === "admin" ? "/admin" : "/claims"}/network`, { auth: true });
  return response.hospitals;
}

export async function createClaimsProvider(payload: { commercial_name: string; type: string; city?: string }): Promise<void> {
  await apiRequest("/claims/network/providers", { method: "POST", auth: true, body: payload });
}


/* Catalogue de packages : configuré par l'Admin (et le Sinistre) pour l'assureur choisi. */

/** Grilles tarifaires : gérées par l'Admin et le Sinistre pour l'assureur choisi. */
function gridsBase(): string {
  return getStoredAccountType() === "admin" ? "/admin/grids" : "/claims/grids";
}

function packagesBase(): string {
  return getStoredAccountType() === "admin" ? "/admin/packages" : "/claims/packages";
}

export type CareCategoryOption = { value: string; label: string };

export type BenefitPeriod = "year" | "years" | "pregnancy" | "stay";

export type PriorApproval = "none" | "always" | "above";

/** Ligne de la table des bénéfices : une prestation du package. */
export type PackageBenefit = {
  id?: number;
  label: string;
  /** Codes actes standards visés (RCS, RPPH…). */
  act_codes: string[];
  /** Affections CIM-10 visées : codes ou plages (« B20-B24, Z34 »). */
  icd_codes: string | null;
  /** Taux de l'acte ; null = couverture de la section. */
  coverage_rate: number | null;
  /** Plafond par acte ou par jour (multiplié par la quantité). */
  unit_ceiling: number | null;
  unit_basis: "act" | "day";
  period_ceiling: number | null;
  max_quantity: number | null;
  period: BenefitPeriod;
  period_years: number | null;
  prior_approval: PriorApproval;
  prior_approval_threshold: number | null;
  age_min: number | null;
  age_max: number | null;
  /** Mention du plan : « Frais réel », « Non garantie »… */
  note: string | null;
};

export type PackageSection = {
  id?: number;
  name: string;
  /** Plafond de la section (ex. Maternité 1 310 $), sur sa périodicité. */
  ceiling_amount: number | null;
  ceiling_period: BenefitPeriod;
  ceiling_period_years: number | null;
  /** Couverture par défaut des actes de la section (%). */
  coverage_rate: number | null;
  /** Codes d'affection non applicables : codes ou plages CIM-10 (« C00-D48, F10 »). */
  excluded_icd_codes: string | null;
  /** Prestataires de la section ; vide = tous ceux du package. */
  hospital_ids: number[];
  /** Délai de carence depuis l'adhésion, en mois. */
  waiting_months: number | null;
  note: string | null;
  benefits: PackageBenefit[];
};

export type InsurancePackage = {
  id: number;
  code: string;
  name: string;
  description: string;
  status: "active" | "inactive";
  currency: string;
  coverage_zone: string | null;
  person_ceiling: number | null;
  family_ceiling: number | null;
  sections: PackageSection[];
  hospital_ids: number[];
  members_count: number;
};

export type PackagePayload = Omit<InsurancePackage, "id" | "hospital_ids" | "members_count">;

export type PackageTemplate = { key: string; name: string; package: PackagePayload };

export type PackagesMeta = {
  packages: InsurancePackage[];
  act_codes: Array<{ code: string; name: string; category: string | null }>;
  periods: Array<{ value: BenefitPeriod; label: string }>;
  prior_approvals: Array<{ value: PriorApproval; label: string }>;
  templates: PackageTemplate[];
};

/** Package de la liste Admin (tous assureurs). */
export type PackageSummary = {
  id: number;
  code: string;
  name: string;
  status: "active" | "inactive";
  coverage_zone: string | null;
  currency: string;
  person_ceiling: number | null;
  insurer_id: number;
  insurer_name: string;
  sections_count: number;
  acts_count: number;
  members_count: number;
};

export async function fetchAllPackages(): Promise<PackageSummary[]> {
  const response = await apiRequest<{ packages: PackageSummary[] }>("/admin/packages-catalogue", { auth: true });
  return response.packages;
}

export async function fetchClaimsPackages(): Promise<PackagesMeta> {
  return apiRequest(packagesBase(), { auth: true });
}

export async function saveClaimsPackage(payload: PackagePayload, id?: number): Promise<InsurancePackage> {
  const response = await apiRequest<{ package: InsurancePackage }>(id ? `${packagesBase()}/${id}` : packagesBase(), {
    method: id ? "PUT" : "POST",
    auth: true,
    body: payload,
  });
  return response.package;
}

export async function setPackageHospitals(id: number, hospitalIds: number[]): Promise<InsurancePackage> {
  const response = await apiRequest<{ package: InsurancePackage }>(`${packagesBase()}/${id}/hospitals`, {
    method: "PUT",
    auth: true,
    body: { hospital_ids: hospitalIds },
  });
  return response.package;
}

/* Compte Assurance : consultation */

export async function fetchInsurerPackages(): Promise<InsurancePackage[]> {
  const response = await apiRequest<{ packages: InsurancePackage[] }>("/insurer/packages", { auth: true });
  return response.packages;
}

/* Grilles tarifaires */

export type GridUnit = "per_act" | "per_day" | "per_hour" | "per_session" | "flat";

export type TariffGridAct = {
  act_code: string;
  act_name: string;
  care_category: string | null;
  care_category_label?: string | null;
  price: number;
  unit: GridUnit;
  unit_label?: string;
};

export type TariffGrid = {
  id: number;
  hospital_id: number;
  hospital_name: string;
  code: string;
  name: string;
  valid_from: string;
  valid_to: string | null;
  currency: "USD" | "CDF";
  status: "active" | "inactive";
  acts_count: number;
  acts?: TariffGridAct[];
};

export type GridPayload = Pick<TariffGrid, "hospital_id" | "code" | "name" | "valid_from" | "valid_to" | "currency" | "status">;

export async function fetchClaimsGrids(hospitalId?: number): Promise<{
  grids: TariffGrid[];
  units: Record<GridUnit, string>;
  care_categories: CareCategoryOption[];
}> {
  return apiRequest(`${gridsBase()}${hospitalId ? `?hospital_id=${hospitalId}` : ""}`, { auth: true });
}

export async function fetchClaimsGrid(id: number): Promise<TariffGrid> {
  const response = await apiRequest<{ grid: TariffGrid }>(`${gridsBase()}/${id}`, { auth: true });
  return response.grid;
}

export async function saveClaimsGrid(payload: GridPayload, id?: number): Promise<TariffGrid> {
  const response = await apiRequest<{ grid: TariffGrid }>(id ? `${gridsBase()}/${id}` : gridsBase(), {
    method: id ? "PUT" : "POST",
    auth: true,
    body: payload,
  });
  return response.grid;
}

export async function saveGridActs(id: number, acts: TariffGridAct[]): Promise<TariffGrid> {
  const response = await apiRequest<{ grid: TariffGrid }>(`${gridsBase()}/${id}/acts`, {
    method: "PUT",
    auth: true,
    body: { acts },
  });
  return response.grid;
}

export async function importGridActs(id: number, file: File): Promise<{ result: { created: number; updated: number; errors: string[] }; grid: TariffGrid }> {
  const form = new FormData();
  form.append("file", file);
  return apiRequest(`${gridsBase()}/${id}/acts/import`, { method: "POST", auth: true, body: form });
}

export async function seedGridFromCatalogue(id: number): Promise<{ message: string; grid: TariffGrid }> {
  return apiRequest(`${gridsBase()}/${id}/acts/from-catalogue`, { method: "POST", auth: true });
}

/* Compte Assurance : réseau et grilles */

export type InsurerNetworkHospital = {
  id: number;
  name: string;
  type: string | null;
  city: string | null;
  province: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  agreement_number: string | null;
  rccm: string | null;
  id_nat: string | null;
};

export async function fetchInsurerNetwork(): Promise<InsurerNetworkHospital[]> {
  const response = await apiRequest<{ hospitals: InsurerNetworkHospital[] }>("/insurer/network/hospitals", { auth: true });
  return response.hospitals;
}

export async function fetchInsurerHospitalGrids(hospitalId: number): Promise<TariffGrid[]> {
  const response = await apiRequest<{ grids: TariffGrid[] }>(`/insurer/network/hospitals/${hospitalId}/grids`, { auth: true });
  return response.grids;
}

export async function fetchInsurerGrid(id: number): Promise<TariffGrid> {
  const response = await apiRequest<{ grid: TariffGrid }>(`/insurer/grids/${id}`, { auth: true });
  return response.grid;
}

/* Audit : bénéficiaire et pathologie de chaque ligne */

export type InvoiceLine = {
  id: number;
  label: string;
  /** Patient tel qu'écrit sur la facture (saisie Admin). */
  patient_label?: string | null;
  patient_ref?: string | null;
  date: string | null;
  quantity: number;
  amount: number;
  care_category: string | null;
  standard_act_code: string | null;
  refaction_code: string | null;
  beneficiary: string | null;
  retained_amount: number | null;
  refaction_reason: string | null;
  refaction_note: string | null;
  /** Prise en charge du package : taux appliqué, montant pris en charge, motif d'une limitation. */
  coverage_rate: number | null;
  covered_amount: number | null;
  coverage_note: string | null;
  /** Fréquentation de l'acte ; « new » = nouvelle fréquentation à créer à l'enregistrement. */
  visit_id: number | "new" | null;
  /** Diagnostic écrit sur la facture. */
  diagnosis_label?: string | null;
  /** Code acte proposé par l'IA, à confirmer. */
  ai_act_code?: string | null;
  /** Patient de la facture absent de la famille du matricule, ou différent du bénéficiaire rattaché. */
  patient_check?: { level: "warning" | "info"; message: string; suggestion: string | null; suggestion_label: string | null } | null;
};

/** Règles du package du patient pour un séjour (hospitalisation). */
export type StayRules = {
  name: string;
  currency: string;
  rules: Array<{
    label: string;
    section: string;
    rate: number | null;
    unit_ceiling: number | null;
    unit_basis: string;
    period_ceiling: number | null;
    max_quantity: number | null;
    prior_approval: string;
  }>;
};

/** Passage du patient : une date de soins (ambulatoire) ou un séjour (hospitalisation). */
export type InvoiceVisit = {
  id: number;
  beneficiary: string;
  patient: string;
  kind: "ambulatory" | "hospitalisation";
  start_date: string;
  end_date: string;
  days: number | null;
  lines: number;
  billed: number;
  retained: number;
  covered: number;
  diagnosis_on_invoice: string | null;
  codes: Array<{ code: string; label: string }>;
  /** Codes CIM-10 proposés par l'IA et pas encore acceptés. */
  suggestions: Array<{ code: string; label: string; reason: string }>;
  /** null pour l'ambulatoire ; pour un séjour, null aussi si l'agent n'a pas de package. */
  package: StayRules | null;
};

export type StandardActOption = { code: string; name: string; category: string | null };

/** Actes d'un patient regroupés par code standard (RCS, RB, RPPH…). */
export type PatientCoding = {
  beneficiary: string | null;
  name: string;
  matricule: string | null;
  company?: string | null;
  agent: string | null;
  lines: number;
  billed: number;
  retained: number;
  refracted: number;
  /** Montant pris en charge par l'assureur (couverture et plafonds du package). */
  covered: number;
  codes: Array<{ code: string; name: string; lines: number; billed: number; retained: number }>;
  refactions: Array<{ code: string; amount: number }>;
};

/** coding → pending (chez le Sinistre) → controlled ou anomaly (retour à l'Audit). */
export type CodingWorkflow = {
  state: "coding" | "pending" | "controlled" | "anomaly";
  editable: boolean;
  uncoded: number;
  /** Fréquentations sans code d'affection CIM-10. */
  missing_affections: number;
  claims_notes: string;
  can_approve: boolean;
};

export type InvoiceLinesResponse = {
  lines: InvoiceLine[];
  beneficiaries: Array<{ key: string; label: string }>;
  /** Facture du prestataire couvrant plusieurs entreprises : patients à chercher dans tout le portefeuille. */
  multi_company?: boolean;
  refaction_reasons: Record<string, string>;
  standard_acts: StandardActOption[];
  refaction_codes: StandardActOption[];
  summary: PatientCoding[];
  /** Codes d'affection CIM-10 par patient (plusieurs possibles), visibles par l'Audit uniquement. */
  affections: InvoiceAffection[];
  visits: InvoiceVisit[];
  workflow: CodingWorkflow;
  message?: string;
};

export type InvoiceAffection = { beneficiary: string; visit_id?: number | null; code: string; label: string };

export type Icd10Code = { code: string; label: string; chapter: string | null };

export async function searchInvoicePatients(invoiceId: number, term: string): Promise<Array<{ key: string; label: string }>> {
  const response = await apiRequest<{ patients: Array<{ key: string; label: string }> }>(
    `/auditor/invoices/${invoiceId}/patients?q=${encodeURIComponent(term)}`,
    { auth: true },
  );
  return response.patients;
}

export async function searchIcd10(term: string): Promise<Icd10Code[]> {
  const response = await apiRequest<{ codes: Icd10Code[] }>(`/auditor/icd10?q=${encodeURIComponent(term)}`, { auth: true });
  return response.codes;
}

export async function saveInvoiceAffections(
  invoiceId: number,
  affections: Array<{ visit_id?: number; beneficiary?: string; icd_code: string }>,
): Promise<InvoiceLinesResponse> {
  return apiRequest(`/auditor/invoices/${invoiceId}/affections`, { method: "PUT", auth: true, body: { affections } });
}

export async function fetchInvoiceLines(invoiceId: number): Promise<InvoiceLinesResponse> {
  return apiRequest(`/auditor/invoices/${invoiceId}/lines`, { auth: true });
}

export async function saveInvoiceLines(
  invoiceId: number,
  lines: Array<{
    line_id: number;
    beneficiary: string | null;
    retained_amount: number | null;
    refaction_reason: string | null;
    refaction_note: string | null;
    refaction_code: string | null;
    standard_act_code: string | null;
    visit_id?: number | "new" | null;
  }>,
): Promise<InvoiceLinesResponse> {
  return apiRequest(`/auditor/invoices/${invoiceId}/lines`, { method: "PUT", auth: true, body: { lines } });
}

/** Suggestions de l'IA : codes CIM-10 par fréquentation et code acte des lignes non codées (rien n'est appliqué). */
export async function suggestInvoiceCodes(invoiceId: number): Promise<InvoiceLinesResponse> {
  return apiRequest(`/auditor/invoices/${invoiceId}/suggest-codes`, { method: "POST", auth: true });
}

/** Transmet la facture codée au Sinistre, avec la note de synthèse. */
export async function submitInvoiceToControl(invoiceId: number, auditNotes: string): Promise<InvoiceLinesResponse> {
  return apiRequest(`/auditor/invoices/${invoiceId}/submit-control`, {
    method: "POST",
    auth: true,
    body: { audit_notes: auditNotes },
  });
}

/* Compte Assurance : rapport de sinistralité */

export type ReportStats = { acts: number; requested: number; validated: number; average: number; share: number };

export type ClaimsReport = {
  key_figures: {
    extraction_date: string;
    period: { from: string; to: string };
    companies: string[];
    population: { agents: number; spouses: number; children: number; others: number };
    requested_total: number;
    validated_total: number;
    rejection_rate: number | null;
    lines_count: number;
    unassigned_lines: number;
  };
  by_status: Array<{ key: string; label: string; requested: number; validated: number; share: number }>;
  by_package: Array<ReportStats & { label: string; beneficiaries: number }>;
  by_nature: Array<ReportStats & { key: string; label: string; providers: Array<ReportStats & { label: string }> }>;
  by_provider: Array<ReportStats & { label: string; beneficiaries: number }>;
  by_company: Array<ReportStats & { label: string; beneficiaries: number }>;
  by_pathology: Array<ReportStats & { code: string | null; label: string; cases: number }>;
  by_family: Array<ReportStats & { name: string; dependants: number }>;
};

export type ConsumptionPerson = {
  key: string;
  type: "employee" | "dependant";
  id: number;
  employee_id: number;
  name: string;
  relation: string;
  matricule: string;
  company_matricule: string;
  company_id: number;
  company: string;
  package: string | null;
  province: string;
  gender: string | null;
  status: "active" | "inactive";
  consumed: number;
  ceiling: number | null;
  percent: number | null;
};

export type ConsumptionFamily = {
  employee_id: number;
  name: string;
  matricule: string;
  company: string;
  package: string | null;
  period: { from: string; to: string };
  members_count: number;
  members: ConsumptionPerson[];
  consumed: number;
  ceiling: number | null;
  percent: number | null;
};

export type InsurerConsumption = { people: ConsumptionPerson[]; families: ConsumptionFamily[] };

export async function fetchInsurerConsumption(): Promise<InsurerConsumption> {
  return apiRequest("/insurer/consumption", { auth: true });
}

export type ClaimsReportFilters = { package: string; provider: string; nature: string };

export async function fetchClaimsReport(
  from: string,
  to: string,
  companyId?: number,
  filters?: Partial<ClaimsReportFilters> & { ceiling?: string },
): Promise<ClaimsReport> {
  const params = new URLSearchParams({ from, to });
  if (companyId) params.set("company_id", String(companyId));
  const { ceiling, ...others } = filters ?? {};
  Object.entries(others).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  if (ceiling === "gt100") params.set("ceiling", "101");
  else if (ceiling) {
    params.set("ceiling", ceiling);
    if (ceiling === "100") params.set("ceiling_inclusive", "1");
  }
  return apiRequest(`/insurer/report?${params.toString()}`, { auth: true });
}

/* Bordereaux mensuels */

export type Settlement = { hospital_id: number; hospital: string; invoices: number; billed: number; rejected: number; to_pay: number };

export async function fetchSettlements(month: string): Promise<Settlement[]> {
  const response = await apiRequest<{ settlements: Settlement[] }>(`/claims/settlements?month=${month}`, { auth: true });
  return response.settlements;
}


/* Tableau de bord décisionnel de l'assureur */

export type DecisionDashboard = {
  year: number;
  pipeline: Array<{ key: string; label: string; count: number; amount: number; overdue: number }>;
  finance: {
    billed: number;
    to_pay: number;
    savings: number;
    savings_rate: number;
    patients: number;
    cost_per_patient: number;
    average_processing_days: number | null;
  };
  payment_notes: {
    sent: number;
    sent_amount: number;
    pending: number;
    pending_amount: number;
    latest: Array<{ invoice_id: number; hospital: string; to_pay: number; validated_at: string | null; sent_at: string | null }>;
  };
  monthly: Array<{ month: string; billed: number; validated: number }>;
  to_pay_by_provider: Array<{ label: string; to_pay: number; monthly: number[] }>;
  by_company: Array<{ label: string; validated: number; patients: number }>;
  requests: { pending: number; pending_amount: number; urgent_pending: number; approved: number; rejected: number };
};

export async function fetchDecisionDashboard(year: number): Promise<DecisionDashboard> {
  return apiRequest(`/insurer/decision-dashboard?year=${year}`, { auth: true });
}

/** Sinistre : enregistre l'envoi de la note de paiement à l'assureur. */
export async function markPaymentNoteSent(invoiceId: number): Promise<string> {
  const response = await apiRequest<{ payment_note_sent_at: string }>(`/claims/invoices/${invoiceId}/payment-note/sent`, {
    method: "POST",
    auth: true,
  });
  return response.payment_note_sent_at;
}

/* Délais de traitement (Admin) */

export type DeadlineSetting = {
  stage: string;
  label: string;
  actor: string;
  default_days: number;
  /** Délai propre à l'assureur (null = délai par défaut). */
  days: number | null;
};

export async function fetchDeadlines(insurerId?: number): Promise<DeadlineSetting[]> {
  const response = await apiRequest<{ deadlines: DeadlineSetting[] }>(
    `/admin/deadlines${insurerId ? `?insurer_id=${insurerId}` : ""}`,
    { auth: true },
  );
  return response.deadlines;
}

export async function saveDeadlines(days: Record<string, number | null>, insurerId?: number): Promise<DeadlineSetting[]> {
  const response = await apiRequest<{ deadlines: DeadlineSetting[] }>("/admin/deadlines", {
    method: "PUT",
    auth: true,
    body: { insurer_id: insurerId ?? null, days },
  });
  return response.deadlines;
}
